/**
 * Ranked-bar-chart image export for the Investments Widget.
 *
 * The chart is plain DOM (no SVG), so the export redraws it from its data
 * onto an offscreen canvas at 2x — title, optional drill breadcrumb, the
 * ranked bars with their type segments, and the type legend chips — matching
 * the on-screen layout and the current filter/selection state, then triggers
 * a PNG download.
 */

import { downloadCanvasPng, resolveExportTokens, roundedRectPath, sourceText } from './canvasExport';
import { t } from '../i18n';
import type { ExportTokens } from './canvasExport';

export interface ChartExportSegment {
  color: string;
  value: number;
}

export interface ChartExportRow {
  label: string;
  value: number;
  /** Preformatted display value ("1,2 Mrd. €" / "42"). */
  valueLabel: string;
  segments: ChartExportSegment[];
  /** A selection exists and it isn't this row. */
  dimmed?: boolean;
  /** This row is the selected one. */
  highlighted?: boolean;
}

export interface ChartExportLegendItem {
  label: string;
  color: string;
  dimmed?: boolean;
}

interface ChartExportOptions {
  title: string;
  /** Active metric shown beside the title ("Volumen" / "Anzahl"). */
  modeLabel: string;
  /** Drill path (industry chart); rendered under the title when present. */
  breadcrumb?: string[];
  rows: ChartExportRow[];
  maxValue: number;
  legend: ChartExportLegendItem[];
}

const SCALE = 2;
const WIDTH = 1000;
const PAD = 28;
const TITLE_H = 40;
const BREADCRUMB_H = 22;
const ROW_H = 38;
const BAR_H = 24;
const LABEL_W = 250;
const LABEL_GAP = 16;
const VALUE_W = 110;
const VALUE_GAP = 12;
const LEGEND_TOP_GAP = 20;
const CHIP_H = 26;
const CHIP_GAP = 8;
// Footer source line, matching the budget widget's chart exports
// (11px sans, bottom-left).
const SOURCE_GAP = 20;
const SOURCE_SIZE = 11;

/** Greedy word wrap capped at `maxLines`; the last line gets an ellipsis. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines) break;
    } else {
      line = candidate;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);

  if (lines.length > maxLines || (lines.length === maxLines && line && !lines.includes(line))) {
    lines.length = maxLines;
    let last = lines[maxLines - 1]!;
    while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = `${last}…`;
  }
  return lines;
}

interface ChipLayout {
  item: ChartExportLegendItem;
  w: number;
}

function legendLines(ctx: CanvasRenderingContext2D, tokens: ExportTokens, legend: ChartExportLegendItem[], maxWidth: number): ChipLayout[][] {
  ctx.font = `13px ${tokens.sansStack}`;
  const chips: ChipLayout[] = legend.map(item => ({
    item,
    // 10px dot + 6px gap + text + 2 × 10px padding
    w: Math.ceil(10 + 6 + ctx.measureText(item.label).width + 20),
  }));
  const lines: ChipLayout[][] = [];
  let line: ChipLayout[] = [];
  let lineW = 0;
  for (const chip of chips) {
    const next = lineW === 0 ? chip.w : lineW + CHIP_GAP + chip.w;
    if (line.length > 0 && next > maxWidth) {
      lines.push(line);
      line = [chip];
      lineW = chip.w;
    } else {
      line.push(chip);
      lineW = next;
    }
  }
  if (line.length > 0) lines.push(line);
  return lines;
}

/**
 * Renders the ranked bar chart to a PNG and triggers a download. `container`
 * is any element inside the widget, used to resolve the theme's CSS
 * variables to literal canvas values.
 */
export async function exportRankedBarChartImage(container: HTMLElement, opts: ChartExportOptions): Promise<void> {
  const tokens = resolveExportTokens(container);

  // Measurement context (canvas fonts must be set before measuring).
  const canvas = document.createElement('canvas');
  const measureCtx = canvas.getContext('2d');
  if (!measureCtx) throw new Error('Canvas 2D context unavailable');

  const contentW = WIDTH - PAD * 2;
  const breadcrumbH = opts.breadcrumb && opts.breadcrumb.length > 0 ? BREADCRUMB_H : 0;
  const chipLines = legendLines(measureCtx, tokens, opts.legend, contentW);
  const legendH = chipLines.length > 0 ? LEGEND_TOP_GAP + chipLines.length * (CHIP_H + CHIP_GAP) - CHIP_GAP : 0;
  const height = PAD + TITLE_H + breadcrumbH + 8 + opts.rows.length * ROW_H + legendH + SOURCE_GAP + SOURCE_SIZE + PAD;

  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = tokens.surface;
  ctx.fillRect(0, 0, WIDTH, height);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  // Title (serif 30/400 like ChartCardTitle) + active metric acting as the
  // value column's header: left-aligned with the values below it.
  ctx.font = `400 30px ${tokens.serifStack}`;
  ctx.fillStyle = tokens.textPrimary;
  const titleBaseline = PAD + 30;
  ctx.fillText(opts.title, PAD, titleBaseline);
  ctx.font = `13px ${tokens.sansStack}`;
  ctx.fillStyle = tokens.textSecondary;
  ctx.fillText(opts.modeLabel, WIDTH - PAD - VALUE_W, titleBaseline);

  // Breadcrumb (drill path), muted with the current level emphasized.
  if (opts.breadcrumb && opts.breadcrumb.length > 0) {
    let x = PAD;
    const y = PAD + TITLE_H + 14;
    opts.breadcrumb.forEach((seg, i) => {
      if (i > 0) {
        ctx.font = `12px ${tokens.sansStack}`;
        ctx.fillStyle = tokens.textSecondary;
        ctx.fillText(' › ', x, y);
        x += ctx.measureText(' › ').width;
      }
      const isLast = i === opts.breadcrumb!.length - 1;
      ctx.font = `${isLast ? 500 : 400} 12px ${tokens.sansStack}`;
      ctx.fillStyle = isLast ? tokens.textPrimary : tokens.textSecondary;
      ctx.fillText(seg, x, y);
      x += ctx.measureText(seg).width;
    });
  }

  // Rows
  const rowsTop = PAD + TITLE_H + breadcrumbH + 8;
  const trackX = PAD + LABEL_W + LABEL_GAP;
  const trackW = contentW - LABEL_W - LABEL_GAP - VALUE_W - VALUE_GAP;

  opts.rows.forEach((row, i) => {
    const rowY = rowsTop + i * ROW_H;
    const rowMidY = rowY + ROW_H / 2;

    if (row.highlighted) {
      // Mirror the on-screen selection: tinted band + left accent.
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = tokens.dataColor;
      ctx.fillRect(PAD - 8, rowY + 1, contentW + 16, ROW_H - 2);
      ctx.globalAlpha = 1;
      ctx.fillStyle = tokens.dataColor;
      ctx.fillRect(PAD - 8, rowY + 1, 2, ROW_H - 2);
    }

    ctx.globalAlpha = row.dimmed ? 0.4 : 1;

    // Label, up to two lines like the on-screen clamp.
    ctx.font = `12px ${tokens.sansStack}`;
    ctx.fillStyle = tokens.textPrimary;
    ctx.textBaseline = 'middle';
    const lines = wrapText(ctx, row.label, LABEL_W, 2);
    const lineH = 15;
    const labelTop = rowMidY - ((lines.length - 1) * lineH) / 2;
    lines.forEach((l, li) => ctx.fillText(l, PAD, labelTop + li * lineH));

    // Bar: overall length encodes the metric, inner segments the type split,
    // hairline separators in the surface color between segments.
    const barW = Math.max(3, opts.maxValue > 0 ? (row.value / opts.maxValue) * trackW : 0);
    const barY = rowMidY - BAR_H / 2;
    ctx.save();
    roundedRectPath(ctx, trackX, barY, barW, BAR_H, 2);
    ctx.clip();
    let segX = trackX;
    row.segments.forEach((seg, si) => {
      const segW = row.value > 0 ? (seg.value / row.value) * barW : 0;
      ctx.fillStyle = seg.color;
      ctx.fillRect(segX, barY, segW, BAR_H);
      segX += segW;
      if (si < row.segments.length - 1) {
        ctx.fillStyle = tokens.surface;
        ctx.fillRect(segX - 1, barY, 1, BAR_H);
      }
    });
    ctx.restore();

    // Value in the fixed right column, left-aligned like on screen.
    ctx.font = `600 14px ${tokens.sansStack}`;
    ctx.fillStyle = tokens.textPrimary;
    ctx.fillText(row.valueLabel, trackX + trackW + VALUE_GAP, rowMidY);

    ctx.globalAlpha = 1;
    ctx.textBaseline = 'alphabetic';
  });

  // Type legend: centered white pill chips, wrapping like on screen.
  let chipY = rowsTop + opts.rows.length * ROW_H + LEGEND_TOP_GAP;
  ctx.textBaseline = 'middle';
  for (const line of chipLines) {
    const lineW = line.reduce((s, c) => s + c.w, 0) + (line.length - 1) * CHIP_GAP;
    let chipX = PAD + (contentW - lineW) / 2;
    for (const chip of line) {
      ctx.globalAlpha = chip.item.dimmed ? 0.4 : 1;
      roundedRectPath(ctx, chipX, chipY, chip.w, CHIP_H, CHIP_H / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(chipX + 10 + 5, chipY + CHIP_H / 2, 5, 0, Math.PI * 2);
      ctx.fillStyle = chip.item.color;
      ctx.fill();
      ctx.font = `13px ${tokens.sansStack}`;
      ctx.fillStyle = tokens.textPrimary;
      ctx.fillText(chip.item.label, chipX + 10 + 10 + 6, chipY + CHIP_H / 2);
      ctx.globalAlpha = 1;
      chipX += chip.w + CHIP_GAP;
    }
    chipY += CHIP_H + CHIP_GAP;
  }

  // Source credit, bottom-left.
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = `${SOURCE_SIZE}px ${tokens.sansStack}`;
  ctx.fillStyle = tokens.textPrimary;
  ctx.fillText(sourceText(), PAD, height - PAD);

  const slug = opts.title
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  await downloadCanvasPng(canvas, `${t().exports.chartFilePrefix}_${slug}`);
}
